import clsx from "clsx";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExpressCheckoutElement, PaymentElement } from "@stripe/react-stripe-js";

import MigiriLogo from "../../components/svg/MigiriLogo";
import StripeLogo from "../../components/svg/StripeLogo";
import {
  stripeExpressCheckoutPaymentOptions,
  stripeExpressCheckoutTrialOptions,
  stripePaymentOptions
} from "../../lib/stripe/elementSetupOptions.tsx";
import { captureMessage } from "../../lib/sentry.ts";
import { FeaturesList } from "../../components/FeaturesList/FeaturesList.tsx";
import { SubscriptionData } from "../../lib/mappers/subscriptionData";
import usePaymentAttempt, { successReturnUrl } from "../../hooks/usePaymentAttempt";
import { useWindowResizeListener } from "../../hooks/useWindowResizeListener.ts";

import FreeTrialCard from "./FreeTrialCard";
import { scrollIntoView } from "./handlers";
import PaymentCard from "./PaymentCard";
import StartTrialButton from "./StartTrialButton";
import "./FreeTrial.css";

const stripeExpressCheckoutOptions = (subscriptionData: SubscriptionData) => {
  if (subscriptionData.isPaymentRequired()) {
    return stripeExpressCheckoutPaymentOptions('white')
  } else {
    return stripeExpressCheckoutTrialOptions({
      subscriptionData: subscriptionData,
      trialStartDate: new Date(),
      theme: 'white',
    })
  }
};

const FreeTrial = ({
  freeTrialActive,
  subscriptionData,
}: {
  freeTrialActive: boolean;
  subscriptionData: SubscriptionData;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { setupAttemptHandler, paymentAttemptHandler } = usePaymentAttempt();
  const [expressCheckoutMounted, setExpressCheckoutMounted] = useState(false);
  const [expressCheckoutVisible, setExpressCheckoutVisible] = useState(false);
  const isDesktop = useWindowResizeListener()

  useEffect(() => {
    if (expressCheckoutMounted) {
      setExpressCheckoutVisible(true);
    }
  }, [expressCheckoutMounted]);

  const submitHandler = subscriptionData.isPaymentRequired()
    ? paymentAttemptHandler
    : setupAttemptHandler;

  const stripeExpressCheckoutElementOptions = stripeExpressCheckoutOptions(subscriptionData);
  const stripePaymentElementOptions = stripePaymentOptions;

  const expressCheckoutElement = <ExpressCheckoutElement
    options={stripeExpressCheckoutElementOptions}
    onReady={(event) => {
      // If payment methods are available, set expressCheckoutMounted to true
      if (event.availablePaymentMethods) {
        setExpressCheckoutMounted(true);
      }
    }}
    onConfirm={async () => {
      const error = await submitHandler();
      if (error === undefined) {
        navigate(successReturnUrl);
      } else {
        captureMessage(`ExpressCheckout submitHandler ${error}`)
      }
    }}
  />

  return (
    <>
      <div className="slanted-reflection-wrapper">
        <div className="slanted-reflection" />
      </div>

      <div className={clsx("FreeTrialPage", "free-trial-content", "content")}>
        <div className="contentWrapper">
          <div className="infoSection">
            <div className="migiriLogo">
              <MigiriLogo />
            </div>
            <h2 className="semibold">
              {freeTrialActive
                ? "Your first week is on us"
                : "Give yourself the chance to transform your life"}
            </h2>

            <FeaturesList />

            {!isDesktop && subscriptionData.isPaymentRequired() && (
              <PaymentCard paymentData={subscriptionData.getPaymentData()} />
            )}

            {!isDesktop && !subscriptionData.isPaymentRequired() && (
              <FreeTrialCard
                freeDays={subscriptionData.getDaysAmount()}
                paymentData={subscriptionData.getPaymentData()}
                freeTrialActive={freeTrialActive}
              />
            )}

            {!isDesktop &&
              <div className={clsx("paymentButtonWrapper", expressCheckoutVisible && "visible")}>
                {expressCheckoutElement}
              </div>
            }

            {!isDesktop && expressCheckoutMounted && (
              <div className="divider">or try with card</div>
            )}
          </div>

          <div className="paymentSection" ref={ref}>
            {isDesktop && subscriptionData.isPaymentRequired() && (
              <PaymentCard paymentData={subscriptionData.getPaymentData()} />
            )}

            {isDesktop && !subscriptionData.isPaymentRequired() && (
              <FreeTrialCard
                freeDays={subscriptionData.getDaysAmount()}
                paymentData={subscriptionData.getPaymentData()}
                freeTrialActive={freeTrialActive}
              />
            )}

            {isDesktop && expressCheckoutElement}

            {isDesktop && expressCheckoutMounted && (
              <div className="divider">or try with card</div>
            )}

            <form onSubmit={submitHandler} style={{ overflow: "hidden" }}>
              <div className="formWrapper">
                <PaymentElement options={stripePaymentElementOptions} />

                <div className="startTrialWrapper">

                  <StartTrialButton
                    freeDays={
                      subscriptionData.isPaymentRequired()
                        ? 0
                        : subscriptionData.getDaysAmount()
                    }
                    freeTrialActive={freeTrialActive}
                    onClick={async (e) => {
                      e.preventDefault();
                      const error = await submitHandler();
                      if (error !== undefined) {
                        scrollIntoView(ref.current!);
                      } else {
                        navigate(successReturnUrl);
                      }
                    }}
                  />
                </div>

                <p className={clsx("securedByStripe", "caption-sm")} >
                  Secured by&nbsp;
                  <StripeLogo />
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FreeTrial;